/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { Controller } from "react-hook-form";

import {
  Icon,
  IPersonaProps,
  IPickerItemProps,
  Label,
  NormalPeoplePicker,
  PeoplePickerItem,
  TooltipHost,
  ValidationState,
} from "@fluentui/react";

import { FlexCol, FlexRow } from "Components/Common/Layout/Layout";
import { HookErrorMessage } from "Components/ReactHookForm/HookErrorMessage/HookErrorMessage";
import { peoplePickerSuggestionProps } from "Constants/PeoplePickerConstants";

import { IPeoplePickerControllerProps } from "./IPeoplePickerControllerProps";
import { getIconStyles, peoplePickerStyles } from "./PeoplePickerController.styles";

/**
 * PeoplePickerController.
 * @component
 * @param {IPeoplePickerControllerProps} props The component props.
 * @returns {React.ReactElement} PeoplePickerController
 */
const PeoplePickerController = (props: IPeoplePickerControllerProps): React.ReactElement => {
  const {
    control,
    defaultSelectedKey = "",
    errors,
    hasTooltip,
    inputName,
    isDisabled,
    isRequired,
    labelStyles,
    labelDisplayName,
    toolTipDisplayText,
    options,
    keyPropertyName,
  } = props;

  const [suggestedOptions, setSuggestedOptions] = useState<IPersonaProps[]>(options);
  const [defaultPersonas, setDefaultPersonas] = useState<IPersonaProps[]>([]);

  useEffect(() => {
    /**
     * Accessibility fix - add aria label to combobox
     * element of TagPicker component.
     */
    const comboboxElement = Array.from(document.getElementsByClassName("ms-ComboBox-Input"));

    const addAriaLabel = document.createAttribute("aria-label");
    addAriaLabel.value = props.labelDisplayName || props.inputName;

    if (comboboxElement && comboboxElement.length > 0) {
      comboboxElement[0].setAttributeNode(addAriaLabel);
    }

    // Fetch persona for defaultSelectedKey
    if (defaultSelectedKey?.length) {
      setDefaultPersonas(
        options.filter((option) => option[keyPropertyName] === defaultSelectedKey)
      );
    }
  }, []);

  /**
   * Search AAD User Info by the input user email.
   * @param {string}  filterText  the user email.
   * @param {IPersonaProps[] | undefined}  currentPersonas  Already selected persons.
   * @returns {IPersonaPropsExtend[] | Promise<IPersonaPropsExtend[]>} Selectable persons.
   */
  const onFilterChanged = (filterText: string): IPersonaProps[] => {
    if (filterText) {
      return options.filter(
        (option) =>
          option.text?.toLowerCase().startsWith(filterText.toLowerCase()) ||
          option.secondaryText?.toLowerCase().startsWith(filterText.toLowerCase())
      );
    } else {
      return options; // Possibly default to []
    }
  };

  function onInputChange(input: string): string {
    const outlookRegEx = /@.*/g;
    const emailAddress = outlookRegEx.exec(input);

    if (emailAddress && emailAddress[0]) {
      return `${input.substring(0, input.length - emailAddress[0].length)}@microsoft.com`;
    }

    return input;
  }

  /**
   * Render item with secondary text for the NormalPeoplePicker.
   */
  const renderItemWithSecondaryText = (
    props: IPickerItemProps<IPersonaProps>
  ): React.ReactElement => {
    const newProps = {
      ...props,
      item: {
        ...props.item,
        ValidationState: ValidationState.valid,
        showSecondaryText: true,
      },
    };

    return <PeoplePickerItem {...newProps} />;
  };

  return (
    <FlexCol>
      <FlexRow>
        <Label htmlFor={inputName} required={isRequired} styles={labelStyles}>
          {labelDisplayName}
        </Label>
        {hasTooltip && (
          <TooltipHost id={inputName} content={toolTipDisplayText || labelDisplayName}>
            <Icon
              aria-label="information"
              iconName="info"
              role="img"
              styles={getIconStyles(!!isRequired)}
              tabIndex={0}
            />
          </TooltipHost>
        )}
      </FlexRow>
      <FlexRow>
        <Controller
          key={JSON.stringify(defaultPersonas)} // Hack to have default value render after being fetched
          aria-label={labelDisplayName}
          name={inputName}
          control={control}
          render={({ field }): React.ReactElement => (
            <NormalPeoplePicker
              onResolveSuggestions={onFilterChanged}
              onRenderItem={renderItemWithSecondaryText}
              aria-label={labelDisplayName}
              {...props}
              defaultSelectedItems={defaultPersonas}
              onItemSelected={(
                item: IPersonaProps | undefined
              ): PromiseLike<IPersonaProps> | null => {
                // We could set the field to the secondaryText (which is the email address) or the Persona itself.
                field.onChange(keyPropertyName ? item?.[keyPropertyName] : item);
                return item as PromiseLike<IPersonaProps> | null;
              }}
              onInputChange={onInputChange}
              disabled={isDisabled}
              styles={peoplePickerStyles}
              onChange={(items?: IPersonaProps[] | undefined): void => {
                if (items?.length === 0) {
                  field.onChange("");
                }
              }}
              resolveDelay={300}
              itemLimit={1}
              pickerSuggestionsProps={peoplePickerSuggestionProps}
              onEmptyResolveSuggestions={(): IPersonaProps[] => options}
              onRemoveSuggestion={(item: IPersonaProps): void => {
                setSuggestedOptions(suggestedOptions.filter((option) => option.id !== item.id));
              }}
            />
          )}
        />
      </FlexRow>
      <HookErrorMessage errors={errors} inputName={inputName} />
    </FlexCol>
  );
};

export default PeoplePickerController;
